<template>
  <div class="settings-action-plan-list">
    <div v-if="!actionPlansLoading && !plans.length">
      {{ $t('message.NO_ACTION_PLAN') }}
    </div>
    <div class="settings-action-plan-list__wrapper">
      <SettingsActionPlan
        v-for="actionPlan in filteredPlans"
        :key="actionPlan.id"
        :value="actionPlan"
        @input="SET_ACTION_PLAN({ ...defaultParams, ...$event })"
        @delete="deletePlan(actionPlan)"
      />
    </div>
    <button
      class="add-button mt10em"
      @click="SET_ACTION_PLAN({ ...defaultParams, actionDuration: 8, maxActions: 5 })"
    >
      <Icon>{{ addCircleMedium }}</Icon>
      <span>{{ $t('message.ADD_ACTION_PLAN') }}</span>
    </button>
  </div>
</template>

<script>
/**
 * List of action plans of a team or a company.
 * In no props are passed, this will fetch and display the action plans of the current team.
 * Pass config = true to edit the action plans of the current company.
 */
import $ from 'jquery';
import { mapState, mapActions } from 'vuex';
import { cloneDeep } from 'lodash';
// Vuex utils
import { FETCH_ACTION_PLANS, SET_ACTION_PLAN, DELETE_ACTION_PLAN } from '@/store/action-types';
import { GET_ACTION_PLANS } from '@/store/mutation-types';
import { GET_CONFIG_ACTION_PLANS } from '@/store/modules/config/mutation-types';
// Components
import Icon from '@/components/icon.vue';
import SettingsActionPlan from '@/components/settings-action-plan.vue';
// Utils
import { calert } from '@/utils/main';
import { addCircleMedium } from '@/assets/icons';

export default {

  name: 'SettingsActionPlanList',

  components: {
    Icon,
    SettingsActionPlan,
  },

  props: {
    config: Boolean,
    filterFunction: {
      type: Function,
      default: null,
    },
  },

  data() {
    return {
      addCircleMedium,
      plans: [],
      draggedIndex: 0,
    };
  },

  computed: {
    /** Object: Default params for the action plans XHRs */
    defaultParams() {
      return { config: this.config ? 1 : undefined };
    },
    /** Array: Plans after filtering using the props filter function */
    filteredPlans() {
      if (this.filterFunction) {
        return this.plans.filter(this.filterFunction);
      }
      return this.plans;
    },
    ...mapState({
      actionPlans(state) {
        return this.config ? state.config[GET_CONFIG_ACTION_PLANS.stateKey] : state[GET_ACTION_PLANS.stateKey];
      },
      actionPlansLoading(state) {
        return this.config ? state.config[GET_CONFIG_ACTION_PLANS.loadingKey] : state[GET_ACTION_PLANS.loadingKey];
      },
    }),
  },

  watch: {
    actionPlans: {
      deep: true,
      handler() {
        this.plans = cloneDeep(this.actionPlans);
        this.refreshSortable();
      },
    },
  },

  created() {
    if (!this.actionPlansLoading) {
      this.$store.dispatch(FETCH_ACTION_PLANS, this.defaultParams);
    }
  },

  methods: {

    refreshSortable() {
      this.$nextTick(() => {
        $('.settings-action-plan-list__wrapper').sortable({
          items: '.settings-action-plan',
          axis: 'y',
          opacity: 1,
          placeholder: 'settings-action-plan-placeholder',
          update: (event, ui) => {
            const plans = cloneDeep(this.plans);
            const newIndex = ui.item.index();
            plans.splice(newIndex, 0, plans.splice(this.draggedIndex, 1)[0]);
            const order = plans.map(plan => plan.id);
            this.SET_ACTION_PLAN({ ...this.defaultParams, ...plans[0], order });
          },
          start: (event, ui) => {
            this.draggedIndex = ui.item.index();
          },
        });
      });
    },
    /** Prompt the user to confirm the deletion of an action plan */
    deletePlan(plan) {
      calert(this.$t('message.DELETE_ACTION_PLAN_CONFIRM'), [
        {
          title: this.$t('message.DELETE_ACTION_PLAN'),
          action: () => {
            this.DELETE_ACTION_PLAN({ ...this.defaultParams, ...plan });
          },
          sClass: 'red',
        },
        {
          title: this.$t('message.CANCEL'),
        },
      ]);
    },
    ...mapActions({
      SET_ACTION_PLAN,
      DELETE_ACTION_PLAN,
    }),
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.settings-action-plan-list {

  .settings-action-plan-placeholder {
    height: 6em;
    background: $color-droppable-active;
  }

  .settings-action-plan {
    position: relative;
    padding-bottom: 1em;
    margin-top: 1em;
  }
}
</style>
